export const CHECK_REPORTS_FOLDER_NAME = 'Check reports';

/** DB folder name for location/site check PDFs (LOCATION check type) */
export const SITE_CHECK_SUBFOLDER_NAME = 'Site';

const ASSET_CHECK_SUBFOLDER_NAMES = new Set(['Safety', 'Loan', 'Delivery', 'Other']);
const LOCATION_CHECK_SUBFOLDER_NAMES = new Set([SITE_CHECK_SUBFOLDER_NAME]);
const SYSTEM_TYPE_FOLDER_NAMES = new Set([
  'Safety',
  'Loan',
  'Delivery',
  SITE_CHECK_SUBFOLDER_NAME,
  'Other',
]);

export type CheckReportFolderScope = 'asset' | 'location';

export interface NestedDocFolder {
  folder_id: string;
  folder_name: string;
  parent_folder_id?: string | null;
}

export function getCheckReportsRootFolder(folders: NestedDocFolder[]): NestedDocFolder | undefined {
  return folders.find(
    (f) => f.folder_name === CHECK_REPORTS_FOLDER_NAME && !f.parent_folder_id,
  );
}

/** User-facing label for the site/location check subfolder */
export function displayCheckReportSubfolderName(
  folderName: string,
  useSiteTerminology?: boolean,
): string {
  if (folderName === SITE_CHECK_SUBFOLDER_NAME) {
    return useSiteTerminology ? 'Site' : 'Location';
  }
  return folderName;
}

export function folderHasChildren(folderId: string, folders: NestedDocFolder[]): boolean {
  return folders.some((f) => f.parent_folder_id === folderId);
}

export function collectDescendantFolderIds(folderId: string, folders: NestedDocFolder[]): Set<string> {
  const ids = new Set<string>([folderId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const f of folders) {
      if (f.parent_folder_id && ids.has(f.parent_folder_id) && !ids.has(f.folder_id)) {
        ids.add(f.folder_id);
        changed = true;
      }
    }
  }
  return ids;
}

export function isSystemCheckReportFolder(
  folder: NestedDocFolder,
  folders: NestedDocFolder[],
): boolean {
  if (folder.folder_name === CHECK_REPORTS_FOLDER_NAME && !folder.parent_folder_id) return true;
  if (folder.parent_folder_id) {
    const parent = folders.find((f) => f.folder_id === folder.parent_folder_id);
    if (parent?.folder_name === CHECK_REPORTS_FOLDER_NAME && SYSTEM_TYPE_FOLDER_NAMES.has(folder.folder_name)) {
      return true;
    }
  }
  return false;
}

function filterCheckReportSubfolders(
  folders: NestedDocFolder[],
  browseParentId: string,
  scope?: CheckReportFolderScope,
): NestedDocFolder[] {
  if (!scope) return folders;

  const checkReportsRoot = getCheckReportsRootFolder(folders);
  if (!checkReportsRoot || browseParentId !== checkReportsRoot.folder_id) {
    return folders;
  }

  const allowed =
    scope === 'location' ? LOCATION_CHECK_SUBFOLDER_NAMES : ASSET_CHECK_SUBFOLDER_NAMES;

  return folders.filter((f) => allowed.has(f.folder_name));
}

export function buildVisibleFolderEntries<T extends NestedDocFolder>(
  folders: T[],
  documents: { folder_id: string | null }[],
  browseParentId: string | null,
  unfiledFolderId: string,
  scope?: CheckReportFolderScope,
): Array<T & { count: number; hasChildren: boolean }> {
  let visible = folders.filter((f) =>
    browseParentId ? f.parent_folder_id === browseParentId : !f.parent_folder_id,
  );

  if (browseParentId) {
    visible = filterCheckReportSubfolders(visible, browseParentId, scope) as T[];
  }

  const unfiledCount = !browseParentId
    ? documents.filter((d) => !d.folder_id).length
    : 0;

  const entries = visible.map((f) => {
    const hasChildren = folderHasChildren(f.folder_id, folders);
    const count = hasChildren
      ? documents.filter((d) => d.folder_id && collectDescendantFolderIds(f.folder_id, folders).has(d.folder_id)).length
      : documents.filter((d) => d.folder_id === f.folder_id).length;
    return { ...f, count, hasChildren };
  });

  if (unfiledCount > 0) {
    entries.push({
      folder_id: unfiledFolderId,
      folder_name: 'Unfoldered',
      count: unfiledCount,
      hasChildren: false,
    } as T & { count: number; hasChildren: boolean });
  }

  return entries;
}

export function buildFolderPathLabels(
  browseParentId: string | null,
  selectedFolderId: string | null,
  folders: NestedDocFolder[],
  useSiteTerminology?: boolean,
): string[] {
  const labels: string[] = [];
  if (browseParentId) {
    const name = folders.find((f) => f.folder_id === browseParentId)?.folder_name || 'Folder';
    labels.push(name === CHECK_REPORTS_FOLDER_NAME ? name : displayCheckReportSubfolderName(name, useSiteTerminology));
  }
  if (selectedFolderId) {
    const name = folders.find((f) => f.folder_id === selectedFolderId)?.folder_name || 'Files';
    labels.push(displayCheckReportSubfolderName(name, useSiteTerminology));
  }
  return labels;
}
